/**
 * Comunicaciones — contenido del editor (mismos bloques que
 * functions/comunicaciones/lib/contenido.js).
 */

export const MAX_ASUNTO = 60;

/** Enlaces útiles que vienen cargados en cada comunicación (se pueden sacar o editar). */
export const ENLACES_HABITIA = Object.freeze([
  { texto: "App para iPhone (App Store)", url: "https://apps.apple.com/uy/app/habitia/id6776420173" },
  { texto: "App para Android (Google Play)", url: "https://play.google.com/store/apps/details?id=com.habitia.resident" },
  { texto: "Habitia en la web", url: "https://app.habitia.ai" },
  { texto: "Habi por WhatsApp: +598 99 496 513", url: "https://wa.me/59899496513" },
  { texto: "Manuales de uso", url: "https://habitia.ai/manuales/" },
  { texto: "Cómo activar tu cuenta", url: "https://habitia.ai/manuales/activar-tu-cuenta/" },
  { texto: "Cómo usar Habi", url: "https://habitia.ai/manuales/preguntale-a-habi/" },
]);

export const DESTINATARIOS = Object.freeze([
  { valor: "ambos", label: "Propietarios e inquilinos" },
  { valor: "propietarios", label: "Solo propietarios" },
  { valor: "inquilinos", label: "Solo inquilinos" },
]);

export function contenidoNuevo(edificioNombre) {
  return {
    asunto: "",
    titulo: "",
    loImportante: [""],
    pasos: [],
    mediosPago: null,
    secciones: [],
    enlaces: ENLACES_HABITIA.map((e) => ({ ...e })),
    firma: edificioNombre ? `Administración de ${edificioNombre}` : "",
  };
}

/**
 * Contenido que llega del servidor o de Habi → el del editor (siempre con
 * todas las listas, aunque vengan vacías).
 */
export function contenidoDelServidor(c, edificioNombre) {
  const base = contenidoNuevo(edificioNombre);
  if (!c || typeof c !== "object") return base;
  const lista = (v) => (Array.isArray(v) ? v : []);
  return {
    asunto: c.asunto || "",
    titulo: c.titulo || "",
    loImportante: lista(c.loImportante).length ? lista(c.loImportante).map(String) : [""],
    pasos: lista(c.pasos).map((p) => ({ texto: (p && p.texto) || "", fecha: (p && p.fecha) || "" })),
    mediosPago:
      c.mediosPago && lista(c.mediosPago.filas).length
        ? { titulo: c.mediosPago.titulo || "Cómo pagar", filas: c.mediosPago.filas.map((f) => ({ medio: f.medio || "", como: f.como || "", costo: f.costo || "" })) }
        : null,
    secciones: lista(c.secciones).map((s) => ({ titulo: (s && s.titulo) || "", texto: (s && s.texto) || "" })),
    enlaces: Array.isArray(c.enlaces) ? c.enlaces.map((e) => ({ texto: (e && e.texto) || "", url: (e && e.url) || "" })) : base.enlaces,
    firma: c.firma || base.firma,
  };
}

// Dato que falta: "[A COMPLETAR]", "[BANCO]"… (no el texto de un link "[texto](url)").
// Solo lookahead: un lookbehind deja la app en blanco en Safari < 16.4.
const RE_MARCA = /\[([A-ZÁÉÍÓÚÜÑ0-9][A-ZÁÉÍÓÚÜÑ0-9 °º.,_/-]{1,40})\](?!\()/g;

/** Marcas "[A COMPLETAR]" de un texto. */
export const marcasEn = (texto) => String(texto || "").match(RE_MARCA) || [];

/** Cuántos datos faltan en todo el contenido. */
export function contarMarcas(c) {
  if (!c) return 0;
  const textos = [
    c.asunto,
    c.titulo,
    ...(c.loImportante || []),
    ...(c.pasos || []).flatMap((p) => [p.texto, p.fecha]),
    ...((c.mediosPago && c.mediosPago.filas) || []).flatMap((f) => [f.medio, f.como, f.costo]),
    ...(c.secciones || []).flatMap((s) => [s.titulo, s.texto]),
    ...(c.enlaces || []).map((e) => e.texto),
    c.firma,
  ];
  return textos.reduce((n, t) => n + marcasEn(t).length, 0);
}

export const ESTADOS = Object.freeze({
  borrador: { status: "borrador", label: "Borrador" },
  enviando: { status: "en_proceso", label: "Enviando" },
  enviada: { status: "enviada", label: "Enviada" },
});

/** Estado de un canal (WhatsApp o email) → badge. */
export const ESTADO_CANAL = Object.freeze({
  pendiente: { status: "pendiente", label: "Pendiente" },
  enviado: { status: "asm_enviado", label: "Enviado" },
  entregado: { status: "asm_entregado", label: "Entregado" },
  leido: { status: "asm_leido", label: "Leído" },
  fallido: { status: "asm_fallido", label: "Falló" },
  omitido: { status: "asm_omitido", label: "No se envió" },
});

export const MOTIVOS = Object.freeze({
  sin_celular: "sin celular",
  baja_whatsapp: "dio de baja el WhatsApp",
  modo_prueba: "modo prueba",
  plantilla_no_aprobada: "plantilla de WhatsApp sin aprobar",
  sin_email: "sin email",
});

/** ¿Tiene sentido reenviarle? (algún canal falló, o el WhatsApp no salió por la plantilla) */
export const sePuedeReenviar = (e) =>
  !!e &&
  ((e.wa && (e.wa.estado === "fallido" || (e.wa.estado === "omitido" && e.wa.motivo === "plantilla_no_aprobada"))) ||
    (e.mail && e.mail.estado === "fallido"));

export const formatoFecha = (ms) =>
  ms ? new Date(ms).toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
